import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import { DeviceType } from 'shared/models/device-type';

import { useDevices } from 'features/device/devices/hooks/use-devices';
import { DeviceCommandType } from 'features/device/models/device-command-type';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemType } from 'features/systems/domain/models/system';
import SystemDevice from 'features/systems/domain/models/system-device';
import CreateDomipDeviceCommand from 'features/systems/domip/domain/models/create-domip-device-command';
import DomipConfiguration from 'features/systems/domip/domain/models/domip-configuration';
import ReadDomipConfigurationResponse from 'features/systems/domip/domain/models/read-domip-configuration-response';
import {
  useCreateDomipDevice,
  useLazyReadDomipConfiguration,
  useUpdateDomipConfiguration,
} from 'features/systems/domip/domain/queries/domip-queries';

export type DomipProviderState = {
  readDomipConfigurationData: ReadDomipConfigurationResponse | undefined;
  readDomipConfigurationIsLoading: boolean;
  readDomipConfigurationIsFetching: boolean;
  updateDomipConfiguration: (domipConfiguration: DomipConfiguration) => void;
  updateDomipConfigurationIsSuccess: boolean;
  updateDomipConfigurationFromForm: (fieldValues: FieldValues) => void;
  createDomipDevice: (createDomipDeviceCommand: CreateDomipDeviceCommand) => void;
  resetForm: () => void;
  domipPulseNumberHasDevice: (domipPulseNumber: number, outputDevice: boolean) => boolean;
  domipPulseNumberHasLinkedDevice: (domipPulseNumber: number, outputDevice: boolean) => boolean;
  openDeviceDetails: (domipPulseNumber: number, outputDevice: boolean) => void;
  selectedDeviceId: string | undefined;
  getBreadCrumbsForDomipPulseNumber: (domipPulseNumber: number, outputDevice: boolean) => string[];
  getFunctionForDomipOutputPulseNumber: (domipPulseNumber: number) => string | undefined;
  getDomipInputDevicePulseNumber: (domipPulseNumber: number) => SystemDevice | undefined;
};

const useDomipProviderState = (): DomipProviderState => {
  const {
    systemsDetailsHook: { selectedSystem, form, viewingMode, refreshSystem },
  } = useSystemsContextProvider();
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const { openDeviceDetailsPopup } = useDevices();

  const {
    refetch: readDomipConfiguration,
    isLoading: readDomipConfigurationIsLoading,
    data: readDomipConfigurationData,
    isFetching: readDomipConfigurationIsFetching,
  } = useLazyReadDomipConfiguration(selectedSystem?.id ?? '');

  const {
    mutate: updateDomipConfiguration,
    isError: updateDomipConfigurationIsError,
    error: updateDomipConfigurationError,
    isSuccess: updateDomipConfigurationIsSuccess,
  } = useUpdateDomipConfiguration();

  const {
    mutate: createDomipDevice,
    isSuccess: createDomipDeviceIsSuccess,
    data: createDomipDeviceData,
    isError: createDomipDeviceIsError,
    error: createDomipDeviceError,
  } = useCreateDomipDevice();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();

  useEffect(() => {
    if (selectedSystem?.id) {
      readDomipConfiguration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem]);

  const openDeviceDetails = (domipPulseNumber: number, isOutputDevice: boolean): void => {
    if (viewingMode === 'viewing' && selectedSystem) {
      setSelectedDeviceId(
        selectedSystem.devices?.find(
          (x) => x.domipPulseNumber === domipPulseNumber && x.type === (isOutputDevice ? DeviceType.DomipOutput : DeviceType.DomipInput),
        )?.id,
      );
      openDeviceDetailsPopup(false);
    }
  };

  const openDeviceDetailsForDeviceId = (deviceId: string): void => {
    if (viewingMode === 'viewing') {
      setSelectedDeviceId(deviceId);
      openDeviceDetailsPopup(false);
    }
  };

  const updateDomipConfigurationFromForm = (fieldValues: FieldValues): void => {
    if (selectedSystem?.type !== SystemType.Domip) {
      return;
    }

    const updatedDomipConfiguration: DomipConfiguration = {
      systemId: selectedSystem.id,
      hostName: fieldValues.hostName?.trim(),
      dhcpRestartTimeAfterLostContact: fieldValues.dhcpRestartTimeAfterLostContact,
      macAddress: selectedSystem.macAddress,
    };

    updateDomipConfiguration(updatedDomipConfiguration);
  };

  const resetForm = useCallback((): void => {
    const normalizedDhcpValue =
      readDomipConfigurationData?.dhcpRestartTimeAfterLostContact && readDomipConfigurationData.dhcpRestartTimeAfterLostContact > 0
        ? 900
        : 0;

    form.reset({
      ...form.getValues(),
      hostName: readDomipConfigurationData?.hostName,
      dhcpRestartTimeAfterLostContact: normalizedDhcpValue,
    });
  }, [form, readDomipConfigurationData]);

  useEffect(() => {
    if (updateDomipConfigurationIsSuccess) {
      readDomipConfiguration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDomipConfigurationIsSuccess]);

  useEffect(() => {
    if (updateDomipConfigurationIsError && updateDomipConfigurationError) {
      setErrorMessage(updateDomipConfigurationError);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDomipConfigurationIsError]);

  useEffect(() => {
    if (readDomipConfigurationData) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readDomipConfigurationData]);

  useEffect(() => {
    if (createDomipDeviceIsSuccess && createDomipDeviceData) {
      openDeviceDetailsForDeviceId(createDomipDeviceData.id);
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDomipDeviceData]);

  useEffect(() => {
    if (createDomipDeviceIsError && createDomipDeviceError) {
      setErrorMessage(createDomipDeviceError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDomipDeviceIsError]);

  const outputDevicesByPulseNumber = useMemo(() => {
    const map = new Map();
    selectedSystem?.devices
      ?.filter((device) => device.type === DeviceType.DomipOutput)
      .forEach((device) => {
        map.set(device.domipPulseNumber, device);
      });
    return map;
  }, [selectedSystem?.devices]);

  const inputDevicesByPulseNumber = useMemo(() => {
    const map = new Map();
    selectedSystem?.devices
      ?.filter((device) => device.type === DeviceType.DomipInput)
      .forEach((device) => {
        map.set(device.domipPulseNumber, device);
      });
    return map;
  }, [selectedSystem?.devices]);

  const domipPulseNumberHasDevice = (domipPulseNumber: number, outputDevice: boolean): boolean => {
    if (!selectedSystem?.macAddress) {
      return false;
    }

    const device = outputDevice ? outputDevicesByPulseNumber.get(domipPulseNumber) : inputDevicesByPulseNumber.get(domipPulseNumber);
    return !!device?.id;
  };

  const domipPulseNumberHasLinkedDevice = (domipPulseNumber: number, outputDevice: boolean): boolean => {
    if (!domipPulseNumberHasDevice(domipPulseNumber, outputDevice)) {
      return false;
    }

    if (!selectedSystem?.macAddress) {
      return false;
    }

    const devices = outputDevice ? outputDevicesByPulseNumber : inputDevicesByPulseNumber;
    const deviceOrganisationUnitId = devices.get(domipPulseNumber)?.organisationUnitId;

    return !!deviceOrganisationUnitId;
  };

  const getBreadCrumbsForDomipPulseNumber = (domipPulseNumber: number, outputDevice: boolean): string[] => {
    if (!selectedSystem?.macAddress) {
      return [];
    }

    const devices = outputDevice ? outputDevicesByPulseNumber : inputDevicesByPulseNumber;
    const device = devices.get(domipPulseNumber);

    return device?.breadCrumbs ?? [];
  };

  const getFunctionForDomipOutputPulseNumber = (domipPulseNumber: number): string | undefined => {
    if (!selectedSystem?.macAddress) {
      return undefined;
    }

    const device = outputDevicesByPulseNumber.get(domipPulseNumber);
    return device?.commands?.find((x: { type: DeviceCommandType }) => x.type === DeviceCommandType.DomipFunction)?.action;
  };

  const getDomipInputDevicePulseNumber = (domipPulseNumber: number): SystemDevice | undefined => {
    if (!selectedSystem?.macAddress) {
      return undefined;
    }

    return inputDevicesByPulseNumber.get(domipPulseNumber);
  };

  return {
    readDomipConfigurationData,
    readDomipConfigurationIsLoading,
    readDomipConfigurationIsFetching,
    updateDomipConfiguration,
    updateDomipConfigurationIsSuccess,
    updateDomipConfigurationFromForm,
    createDomipDevice,
    resetForm,
    domipPulseNumberHasDevice,
    domipPulseNumberHasLinkedDevice,
    openDeviceDetails,
    selectedDeviceId,
    getBreadCrumbsForDomipPulseNumber,
    getFunctionForDomipOutputPulseNumber,
    getDomipInputDevicePulseNumber,
  };
};

export default useDomipProviderState;
